import React, { useContext, useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';
import ItemSourceMeanValidatorItemsModel from 'models/ItemSourceMeanValidatorItemsModel';
import { ItemSourceMeanValidatorModel } from 'models/ItemSourceMeanValidatorModel';
import { useForceRender } from 'hooks/useForceRender';
import Grid from 'CMFW/grid/Grid';
import DeleteButton from 'CMFW/button/DeleteButton';
import ModuleContext from 'CMFW/context/ModuleContext';
import ValidatorHeader from 'components/ValidatorHeader';
import { ItemSourceMeanValidatorItemStatus as ItemStatus } from 'constants/itemSourceMeanValidatorStatus';
import { IFields } from 'CMFW/grid/fields';
import { compareAiConfidence } from 'CMFW/grid/comparators/compareAiConfidence';
import ItemSourceMeanValidatorSummary from './ItemSourceMeanValidatorSummary';
import ItemSourceMeanValidatorBulkActions from './ItemSourceMeanValidatorBulkActions';
import { itemSourceMeanValidatorItemStatusRenderer } from '../../../CMFW/grid/cellRenderers/itemSourceMeanValidatorItemStatus';
import { StyledTargetSourceRenderer } from '../../../CMFW/grid/cellRenderers/StyledTargetSourceRenderer';

let currentFilter: { type: 'status' | 'decision'; value: string } | null = null;

const ItemSourceMeanValidatorDetails: React.FC = () => {
  const model = useContext(ModuleContext) as ItemSourceMeanValidatorModel;
  const jobId = model.itemId;

  const forceRender = useForceRender();
  const [activeFilter, setActiveFilter] = useState<{ type: 'status' | 'decision'; value: string } | null>(null);
  const [jobName, setJobName] = useState<string>('');
  currentFilter = activeFilter;

  const itemsModel = useMemo(() => new ItemSourceMeanValidatorItemsModel({ render: forceRender }), [forceRender]);
  useEffect(() => {
    if (jobId > 0) {
      itemsModel.setJobId(jobId);
      setActiveFilter(null);
      // Load job details to get the name
      model.loadItemData(jobId).then((data) => {
        if (data && data.name) {
          setJobName(data.name);
        }
      });
    }
  }, [jobId, itemsModel, model]);

  useEffect(() => {
    itemsModel.loadingGrid = true;
    itemsModel.render();
    setTimeout(() => {
      itemsModel.loadingGrid = false;
      itemsModel.render();
    }, 0);
  }, [activeFilter, itemsModel]);

  const isFilterActive = () => currentFilter !== null;
  const isRowVisible = (node: any) => {
    if (!currentFilter) return true;
    const data = node.data;
    if (!data) return true;
    switch (currentFilter.type) {
      case 'status':
        if (currentFilter.value === 'processing') return data.status === ItemStatus.PENDING;
        if (currentFilter.value === 'review') return data.status === ItemStatus.NEEDS_USER_REVIEW;
        if (currentFilter.value === 'error') return data.ai_result?.decision === 'error';
        return true;
      case 'decision':
        // Always filter by AI decision, not user's selection
        return data.ai_result?.decision === currentFilter.value;
      default:
        return true;
    }
  };

  const cellRenderers = useMemo(
    () => ({
      [IFields.itemSourceMeanValidatorActions]: 'itemSourceMeanValidatorActionRenderer',
      [IFields.itemSourceMeanValidatorAiResult]: 'itemSourceMeanValidatorAiResultRenderer',
      [IFields.itemSourceMeanValidatorStatus]: 'itemSourceMeanValidatorItemStatusRenderer',
      [IFields.itemSourceMeanValidatorItem]: StyledTargetSourceRenderer,
      [IFields.itemSourceMeanValidatorMeaning]: 'targetSourceRenderer',
      [IFields.conceptValidatorItemStatus]: itemSourceMeanValidatorItemStatusRenderer,
      [IFields.moduleNavigation]: 'moduleNavigationRenderer',
      [IFields.agent_prompt_log]: 'agentPromptLogRenderer',
    }),
    [],
  );

  const columnSorters = useMemo(() => ({ [IFields.itemSourceMeanValidatorAiResult]: compareAiConfidence }), []);

  const gridOptions = useMemo(
    () => ({
      getRowStyle: (params: any) => {
        if (params.data && typeof params.data.status === 'number') {
          if (params.data.status === ItemStatus.PENDING) return { backgroundColor: '#fffef7' };
          if (params.data.status === ItemStatus.NEEDS_USER_REVIEW) return { backgroundColor: '#fff8f0' };
        }
        return undefined;
      },
    }),
    [],
  );

  // Guard render after establishing hooks to satisfy rules-of-hooks
  if (!jobId || jobId === -1) {
    return null;
  }

  return (
    <Container>
      <ValidatorHeader
        id={jobId}
        name={jobName}
        idLabel="Job"
        bulkActions={
          <ItemSourceMeanValidatorBulkActions itemsModel={itemsModel} model={model} onActionComplete={forceRender} />
        }
        itemsModel={itemsModel}
      />

      <ItemSourceMeanValidatorSummary
        itemsModel={itemsModel}
        onFilterChange={setActiveFilter}
        activeFilter={activeFilter}
        onClearFilter={() => setActiveFilter(null)}
      />

      <GridContainer>
        <ModuleContext.Provider value={itemsModel}>
          <Grid
            cellRenderers={cellRenderers}
            gridOptions={gridOptions}
            isFilterActive={isFilterActive}
            isRowVisible={isRowVisible}
            columnSorters={columnSorters}
          />
        </ModuleContext.Provider>
      </GridContainer>

      <DeleteButtonContainer>
        <ModuleContext.Provider value={model}>
          <DeleteButton />
        </ModuleContext.Provider>
      </DeleteButtonContainer>
    </Container>
  );
};

export default ItemSourceMeanValidatorDetails;

const Container = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
`;
const GridContainer = styled.div`
  flex: 1;
  overflow: auto;
  position: relative;
`;
const DeleteButtonContainer = styled.div`
  display: flex;
  justify-content: flex-start;
  padding-top: 4px;
`;
